import React, { useState, useEffect, useContext, useMemo } from 'react';
import { UserContext } from '../../Context/UserContext';
import './KioskVariantModal.css';

const KioskVariantModal = ({ item, cartItem, onClose, onUpdate, autoOpenNutrition = false }) => {
  const { baseUrl } = useContext(UserContext) || { baseUrl: '' };
  const BASE_URL = baseUrl || '';

  const [loading, setLoading] = useState(true);
  const [fetchedItem, setFetchedItem] = useState(null);
  const [allToppings, setAllToppings] = useState([]);

  // Main item states
  const [selectedSize, setSelectedSize] = useState(cartItem?.selectedSize || null);
  const [kitchenNotes, setKitchenNotes] = useState(cartItem?.kitchenNotes || {});
  const [icePreference, setIcePreference] = useState(cartItem?.icePreference || 'without_ice');
  const [isSpicy, setIsSpicy] = useState(cartItem?.isSpicy || false);
  const [selectedCustomVariants, setSelectedCustomVariants] = useState(cartItem?.selectedCustomVariants || {});
  const [selectedItemToppings, setSelectedItemToppings] = useState(
    cartItem?.itemToppings
      ? Object.keys(cartItem.itemToppings).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      : {}
  );
  const [totalPrice, setTotalPrice] = useState(cartItem?.totalPrice || (item?.standard_selling_rate || 0));

  // Addons & Combos states
  const [addonQuantities, setAddonQuantities] = useState(cartItem?.addonQuantities || {});
  const [addonVariants, setAddonVariants] = useState(cartItem?.addonVariants || {});
  const [addonCustomVariants, setAddonCustomVariants] = useState(cartItem?.addonCustomVariants || {});
  const [addonToppings, setAddonToppings] = useState(cartItem?.addonToppings || {});

  const [comboQuantities, setComboQuantities] = useState(cartItem?.comboQuantities || {});
  const [comboVariants, setComboVariants] = useState(cartItem?.comboVariants || {});
  const [comboCustomVariants, setComboCustomVariants] = useState(cartItem?.comboCustomVariants || {});
  const [comboToppings, setComboToppings] = useState(cartItem?.comboToppings || {});

  // Ingredients and Nutrition states
  const [addonIngredients, setAddonIngredients] = useState({});
  const [comboIngredients, setComboIngredients] = useState({});
  const [showIngredientsModal, setShowIngredientsModal] = useState(false);
  const [selectedSizeFilter, setSelectedSizeFilter] = useState(
    (cartItem?.selectedSize || item.splitSize)
      ? (cartItem?.selectedSize || item.splitSize) === 'S' ? 'small' : (cartItem?.selectedSize || item.splitSize) === 'M' ? 'medium' : 'large'
      : item.variants?.size?.enabled ? 'medium' : null
  );

  // 🥘 Helper: normalise size/cold/spicy from raw addon/combo data 🥘───────────
  // Addons and combos from the API can store size at entity.size OR entity.variants.size
  const getEntityVariants = (entity) => ({
    size: {
      enabled: entity.variants?.size?.enabled ?? entity.size?.enabled ?? false,
      small_price: entity.variants?.size?.small_price ?? entity.size?.small_price ?? 0,
      medium_price: entity.variants?.size?.medium_price ?? entity.size?.medium_price ?? 0,
      large_price: entity.variants?.size?.large_price ?? entity.size?.large_price ?? 0,
    },
    cold: {
      enabled: entity.variants?.cold?.enabled ?? entity.cold?.enabled ?? false,
      ice_price: entity.variants?.cold?.ice_price ?? entity.cold?.ice_price ?? 0,
    },
    spicy: {
      enabled: entity.variants?.spicy?.enabled ?? entity.spicy?.enabled ?? false,
      spicy_price: entity.variants?.spicy?.spicy_price ?? entity.spicy?.spicy_price ?? 30,
    },
  });

  // ─── Auto-open Nutrition Modal ─────────────────────────────────────────────
  useEffect(() => {
    if (autoOpenNutrition && fetchedItem) {
      setShowIngredientsModal(true);
    }
  }, [autoOpenNutrition, fetchedItem]);

  // ─── Fetch Toppings ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchToppings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/toppings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setAllToppings((await res.json()) || []);
      } catch (e) {
        console.error('Error fetching toppings:', e);
      }
    };
    fetchToppings();
  }, [BASE_URL]);

  // ─── Fetch Item Details ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchItemDetails = async () => {
      setFetchedItem(null);
      try {
        const itemName = encodeURIComponent(item.item_name || item.name);
        const token = localStorage.getItem('token');
        const active_company = localStorage.getItem('active_company') || '';
        const active_branch = localStorage.getItem('active_branch') || '';

        const res = await fetch(`${BASE_URL}/api/items/${itemName}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Company-Name': active_company,
            'X-Branch-Name': active_branch,
          },
        });

        if (res.ok) {
          const data = await res.json();
          const fd = {
            ...data,
            item_name: data.item_name || item.item_name || item.name,
            price_list_rate: item.price_list_rate || data.price_list_rate || 100,
            custom_variants: Array.isArray(data.custom_variants) ? data.custom_variants : (item.custom_variants || []),
            addons: Array.isArray(data.addons) ? data.addons : (item.addons || []),
            combos: Array.isArray(data.combos) ? data.combos : (item.combos || []),
            toppings: Array.isArray(data.toppings) ? data.toppings : (item.toppings || []),
            variants: {
              size: {
                enabled: data.variants?.size?.enabled ?? data.size?.enabled ?? item.size?.enabled ?? false,
                small_price: data.variants?.size?.small_price ?? data.size?.small_price ?? item.size?.small_price ?? 0,
                medium_price: data.variants?.size?.medium_price ?? data.size?.medium_price ?? item.size?.medium_price ?? 0,
                large_price: data.variants?.size?.large_price ?? data.size?.large_price ?? item.size?.large_price ?? 0,
              },
              cold: data.variants?.cold || data.cold || item.cold || { enabled: false, ice_price: 0 },
              spicy: {
                enabled: data.variants?.spicy?.enabled ?? data.spicy?.enabled ?? item.spicy?.enabled ?? false,
                spicy_price: data.variants?.spicy?.spicy_price ?? data.spicy?.spicy_price ?? item.spicy?.spicy_price ?? 30,
              },
            },
          };
          setFetchedItem(fd);
          if (!cartItem && fd.variants.size.enabled) setSelectedSize('M');
        } else {
          throw new Error('API fetch failed');
        }
      } catch (e) {
        console.error('Error fetching item details:', e);
        const fd = {
          ...item,
          price_list_rate: item.price_list_rate || item.standard_selling_rate || 100,
          custom_variants: item.custom_variants || [],
          addons: item.addons || [],
          combos: item.combos || [],
          toppings: item.toppings || [],
          variants: {
            size: item.size || { enabled: false, small_price: 0, medium_price: 0, large_price: 0 },
            cold: item.cold || { enabled: false, ice_price: 0 },
            spicy: { enabled: item.spicy?.enabled ?? false, spicy_price: item.spicy?.spicy_price ?? 30 },
          },
        };
        setFetchedItem(fd);
        if (!cartItem && fd.variants.size.enabled) setSelectedSize('M');
      } finally {
        setLoading(false);
      }
    };
    if (item) fetchItemDetails();
  }, [item, cartItem, BASE_URL]);

  // ─── Price Calculation ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!fetchedItem) return;
    let price = fetchedItem.price_list_rate || item.standard_selling_rate || 0;

    // Main item size
    if (fetchedItem.variants?.size?.enabled && selectedSize) {
      if (selectedSize === 'S') price = fetchedItem.variants.size.small_price || price;
      if (selectedSize === 'M') price = fetchedItem.variants.size.medium_price || price;
      if (selectedSize === 'L') price = fetchedItem.variants.size.large_price || price;
    }
    if (fetchedItem.variants?.cold?.enabled && icePreference === 'with_ice')
      price += fetchedItem.variants.cold.ice_price || 0;
    
    if (fetchedItem.variants?.spicy?.enabled && isSpicy)
      price += fetchedItem.variants.spicy.spicy_price || 30;

    fetchedItem.custom_variants?.forEach(v => {
      if (v.enabled) v.subheadings?.forEach(sub => { if (selectedCustomVariants[sub.name]) price += sub.price || 0; });
    });
    Object.entries(selectedItemToppings).forEach(([tid, isSel]) => {
      if (isSel) {
        const t = allToppings.find(t => t._id === tid || t.topping_name === tid);
        if (t) price += t.price || 0;
      }
    });

    // Addons price
    Object.entries(addonQuantities).forEach(([addonName, qty]) => {
      if (qty > 0) {
        const addon = fetchedItem.addons?.find(a => (a.name1 || a.name) === addonName);
        if (addon) {
          const av = getEntityVariants(addon);
          let ap = addon.addon_price || addon.price || 0;
          const sv = addonVariants[addonName] || {};
          if (av.size.enabled && sv.size) ap = av.size[`${sv.size.toLowerCase()}_price`] || ap;
          if (av.cold.enabled && sv.cold) ap += av.cold.ice_price || 0;
          if (av.spicy.enabled && sv.spicy) ap += av.spicy.spicy_price || 30;
          const cv = addonCustomVariants[addonName] || {};
          addon.custom_variants?.forEach(v => { if (v.enabled) v.subheadings?.forEach(s => { if (cv[s.name]) ap += s.price || 0; }); });
          const tops = addonToppings[addonName] || {};
          addon.toppings?.forEach(t => { if (tops[t._id || t.topping_name]) ap += t.price || 0; });
          price += ap * qty;
        }
      }
    });

    // Combos price
    Object.entries(comboQuantities).forEach(([comboName, qty]) => {
      if (qty > 0) {
        const combo = fetchedItem.combos?.find(c => (c.name1 || c.name) === comboName);
        if (combo) {
          const cv2 = getEntityVariants(combo);
          let cp = combo.combo_price || combo.price || 0;
          const sv = comboVariants[comboName] || {};
          if (cv2.size.enabled && sv.size) cp = cv2.size[`${sv.size.toLowerCase()}_price`] || cp;
          if (cv2.cold.enabled && sv.cold) cp += cv2.cold.ice_price || 0;
          if (cv2.spicy.enabled && sv.spicy) cp += cv2.spicy.spicy_price || 30;
          const cVars = comboCustomVariants[comboName] || {};
          combo.custom_variants?.forEach(v => { if (v.enabled) v.subheadings?.forEach(s => { if (cVars[s.name]) cp += s.price || 0; }); });
          const tops = comboToppings[comboName] || {};
          combo.toppings?.forEach(t => { if (tops[t._id || t.topping_name]) cp += t.price || 0; });
          price += cp * qty;
        }
      }
    });

    setTotalPrice(price);
  }, [
    fetchedItem, selectedSize, icePreference, isSpicy, selectedCustomVariants, selectedItemToppings,
    allToppings, addonQuantities, addonVariants, addonCustomVariants, addonToppings,
    comboQuantities, comboVariants, comboCustomVariants, comboToppings, item,
  ]);

  useEffect(() => {
    const fetchAddonAndComboIngredients = async () => {
      if (!fetchedItem) return;
      const newAddonIngredients = {};
      const token = localStorage.getItem('token');
      const active_company = localStorage.getItem('active_company') || '';
      const active_branch = localStorage.getItem('active_branch') || '';
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Company-Name': active_company,
        'X-Branch-Name': active_branch
      };
      for (const addon of fetchedItem.addons || []) {
        try {
          const response = await fetch(
            `${BASE_URL}/api/items/nutrition/${encodeURIComponent(addon.name1 || addon.name)}?type=addon&item_id=${fetchedItem._id}&index=${fetchedItem.addons.indexOf(addon)}`,
            { method: 'GET', headers }
          );
          if (response.ok) {
            const data = await response.json();
            newAddonIngredients[addon.name1 || addon.name] = Array.isArray(data.ingredients) ? data.ingredients.map(ing => ({
              ...ing,
              nutrition: Array.isArray(ing.nutrition) ? ing.nutrition : [],
              weight: ing.weight || '1',
              small: ing.small || '1',
              medium: ing.medium || '1',
              large: ing.large || '1',
            })) : [];
          } else {
            newAddonIngredients[addon.name1 || addon.name] = [];
          }
        } catch (error) {
          console.error(`Error fetching ingredients for add-on ${addon.name1 || addon.name}:`, error);
          newAddonIngredients[addon.name1 || addon.name] = [];
        }
      }
      setAddonIngredients(newAddonIngredients);

      const newComboIngredients = {};
      for (const combo of fetchedItem.combos || []) {
        try {
          const response = await fetch(
            `${BASE_URL}/api/items/nutrition/${encodeURIComponent(combo.name1 || combo.name)}?type=combo&item_id=${fetchedItem._id}&index=${fetchedItem.combos.indexOf(combo)}`,
            { method: 'GET', headers }
          );
          if (response.ok) {
            const data = await response.json();
            newComboIngredients[combo.name1 || combo.name] = Array.isArray(data.ingredients) ? data.ingredients.map(ing => ({
              ...ing,
              nutrition: Array.isArray(ing.nutrition) ? ing.nutrition : [],
              weight: ing.weight || '1',
              small: ing.small || '1',
              medium: ing.medium || '1',
              large: ing.large || '1',
            })) : [];
          } else {
            newComboIngredients[combo.name1 || combo.name] = [];
          }
        } catch (error) {
          console.error(`Error fetching ingredients for combo ${combo.name1 || combo.name}:`, error);
          newComboIngredients[combo.name1 || combo.name] = [];
        }
      }
      setComboIngredients(newComboIngredients);
    };
    fetchAddonAndComboIngredients();
  }, [fetchedItem, BASE_URL]);

  const calculateNutrition = (ingredient, sizeKey, isAddon = false, addonName = null, isCombo = false, comboName = null) => {
    if (!ingredient || !Array.isArray(ingredient.nutrition)) return [];
    const baseWeight = parseFloat(ingredient.weight) || 1;
    let effectiveSizeKey = sizeKey;
    if (isAddon && addonName) {
      const aVars = addonVariants[addonName] || {};
      effectiveSizeKey = aVars.size ? (aVars.size === 'S' || aVars.size === 'Small' ? 'small' : aVars.size === 'M' || aVars.size === 'Medium' ? 'medium' : 'large') : effectiveSizeKey;
    }
    if (isCombo && comboName) {
      const cVars = comboVariants[comboName] || {};
      effectiveSizeKey = cVars.size ? (cVars.size === 'S' || cVars.size === 'Small' ? 'small' : cVars.size === 'M' || cVars.size === 'Medium' ? 'medium' : 'large') : effectiveSizeKey;
    }
    const sizeWeight = parseFloat(ingredient[effectiveSizeKey]) || 1;
    const scalingFactor = baseWeight !== 0 ? sizeWeight / baseWeight : 1;
    return ingredient.nutrition.map(nut => {
      if (!nut || typeof nut.nutrition_value === 'undefined') return '0.000';
      const nutritionValue = parseFloat(nut.nutrition_value) || 0;
      if (nutritionValue === 0) return '0.000';
      const scaledValue = nutritionValue * scalingFactor;
      return Number.isFinite(scaledValue) ? scaledValue.toFixed(3) : '0.000';
    });
  };

  const getAllNutritionNames = useMemo(() => {
    const nutritionNames = new Set();
    const allIngredients = [
      ...(Array.isArray(fetchedItem?.ingredients) ? fetchedItem.ingredients : []),
      ...Object.values(addonIngredients).flat(),
      ...Object.values(comboIngredients).flat(),
    ];
    allIngredients.forEach(ingredient => {
      if (Array.isArray(ingredient.nutrition)) {
        ingredient.nutrition.forEach(nut => {
          if (nut.nutrition_name) nutritionNames.add(nut.nutrition_name);
        });
      }
    });
    return Array.from(nutritionNames);
  }, [fetchedItem, addonIngredients, comboIngredients]);

  const calculateTotalNutritionForSize = (sizeKey, nutritionName) => {
    let total = 0;
    const mainIngredients = Array.isArray(fetchedItem?.ingredients) ? fetchedItem.ingredients : [];
    mainIngredients.forEach(ingredient => {
      if (!Array.isArray(ingredient.nutrition)) return;
      const nutritionValues = calculateNutrition(ingredient, sizeKey);
      const nutritionIndex = ingredient.nutrition.findIndex(nut => nut.nutrition_name === nutritionName);
      if (nutritionIndex !== -1) {
        total += parseFloat(nutritionValues[nutritionIndex]) || 0;
      }
    });
    Object.entries(addonIngredients).forEach(([addonName, ingredients]) => {
      ingredients.forEach(ingredient => {
        if (!Array.isArray(ingredient.nutrition)) return;
        const nutritionValues = calculateNutrition(ingredient, sizeKey, true, addonName);
        const nutritionIndex = ingredient.nutrition.findIndex(nut => nut.nutrition_name === nutritionName);
        if (nutritionIndex !== -1) {
          const value = parseFloat(nutritionValues[nutritionIndex]) || 0;
          const qty = addonQuantities[addonName] || 0;
          total += value * qty;
        }
      });
    });
    Object.entries(comboIngredients).forEach(([comboName, ingredients]) => {
      ingredients.forEach(ingredient => {
        if (!Array.isArray(ingredient.nutrition)) return;
        const nutritionValues = calculateNutrition(ingredient, sizeKey, false, null, true, comboName);
        const nutritionIndex = ingredient.nutrition.findIndex(nut => nut.nutrition_name === nutritionName);
        if (nutritionIndex !== -1) {
          const value = parseFloat(nutritionValues[nutritionIndex]) || 0;
          const qty = comboQuantities[comboName] || 0;
          total += value * qty;
        }
      });
    });
    return total > 0 ? total.toFixed(3) : '0.000';
  };

  const currentSizeFilter = selectedSizeFilter || (
    selectedSize
      ? (selectedSize === 'S' ? 'small' : selectedSize === 'M' ? 'medium' : 'large')
      : 'medium'
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleCustomVariantToggle = n => setSelectedCustomVariants(p => ({ ...p, [n]: !p[n] }));
  const handleToppingToggle = id => setSelectedItemToppings(p => ({ ...p, [id]: !p[id] }));

  const toggleAddon = name => setAddonQuantities(p => ({ ...p, [name]: p[name] ? 0 : 1 }));
  const toggleCombo = name => setComboQuantities(p => ({ ...p, [name]: p[name] ? 0 : 1 }));

  const setAddonVar = (n, t, v) => setAddonVariants(p => ({ ...p, [n]: { ...(p[n] || {}), [t]: v } }));
  const setComboVar = (n, t, v) => setComboVariants(p => ({ ...p, [n]: { ...(p[n] || {}), [t]: v } }));

  const toggleAddonCV = (n, s) => setAddonCustomVariants(p => ({ ...p, [n]: { ...(p[n] || {}), [s]: !p[n]?.[s] } }));
  const toggleComboCV = (n, s) => setComboCustomVariants(p => ({ ...p, [n]: { ...(p[n] || {}), [s]: !p[n]?.[s] } }));

  const toggleAddonTop = (n, tid) => setAddonToppings(p => ({ ...p, [n]: { ...(p[n] || {}), [tid]: !p[n]?.[tid] } }));
  const toggleComboTop = (n, tid) => setComboToppings(p => ({ ...p, [n]: { ...(p[n] || {}), [tid]: !p[n]?.[tid] } }));

  // ─── Save ──────────────────────────────────────────────────────────────────
  const handleSave = () => {
    const customVariantsDetails = {};
    const customVariantsQuantities = {};
    fetchedItem?.custom_variants?.forEach(v => {
      if (v.enabled) v.subheadings?.forEach(sub => {
        if (selectedCustomVariants[sub.name]) {
          customVariantsDetails[sub.name] = { name: sub.name, price: sub.price || 0, heading: v.heading, image: sub.image };
          customVariantsQuantities[sub.name] = 1;
        }
      });
    });
    const itemToppings = Object.entries(selectedItemToppings).reduce((acc, [id, isSel]) => {
      if (isSel) { const t = allToppings.find(t => t._id === id || t.topping_name === id); if (t) acc[id] = { name: t.topping_name, price: t.price || 0 }; }
      return acc;
    }, {});
    const updatedItem = {
      ...item, ...fetchedItem,
      quantity: cartItem?.quantity || cartItem?.qty || 1,
      qty: cartItem?.quantity || cartItem?.qty || 1,
      totalPrice,
      variants: {
        size: fetchedItem?.variants?.size?.enabled ? { selected: selectedSize } : null,
        cold: fetchedItem?.variants?.cold?.enabled ? { icePreference } : null,
        spicy: fetchedItem?.variants?.spicy?.enabled ? { isSpicy } : null,
      },
      selectedSize, icePreference, isSpicy, selectedCustomVariants, customVariantsDetails, customVariantsQuantities,
      itemToppings, selectedItemToppings,
      addonQuantities, addonVariants, addonCustomVariants, addonToppings,
      comboQuantities, comboVariants, comboCustomVariants, comboToppings,
      kitchenNotes,
    };
    updatedItem.totalPrice = totalPrice * updatedItem.qty;
    onUpdate(updatedItem);
  };

  // ─── Render Variant Panel ──────────────────────────────────────────────────
  // Shows size/ice/custom/toppings BELOW a selected addon or combo card
  const renderVariantPanel = (entity, entityName, type) => {
    const ev = getEntityVariants(entity);
    const varsMap    = type === 'addon' ? addonVariants       : comboVariants;
    const cVarsMap   = type === 'addon' ? addonCustomVariants : comboCustomVariants;
    const topsMap    = type === 'addon' ? addonToppings       : comboToppings;
    const setVar     = type === 'addon' ? setAddonVar         : setComboVar;
    const toggleCV   = type === 'addon' ? toggleAddonCV       : toggleComboCV;
    const toggleTop  = type === 'addon' ? toggleAddonTop      : toggleComboTop;

    const hasSize    = ev.size.enabled;
    const hasCold    = ev.cold.enabled;
    const hasSpicy   = ev.spicy.enabled;
    const hasCustom  = entity.custom_variants?.some(v => v.enabled && v.subheadings?.length > 0);
    const hasToppings = entity.toppings?.length > 0;

    if (!hasSize && !hasCold && !hasSpicy && !hasCustom && !hasToppings) return null;

    return (
      <div className="kvm-variant-panel">
        {/* Size */}
        {hasSize && (
          <div className="kvm-vp-section">
            <div className="kvm-vp-title">Size</div>
            <div className="kvm-vp-size-grid">
              {[
                { label: 'Small',  key: 'Small',  price: ev.size.small_price },
                { label: 'Medium', key: 'Medium', price: ev.size.medium_price },
                { label: 'Large',  key: 'Large',  price: ev.size.large_price },
              ].map(({ label, key, price }) => {
                const activeKey = varsMap[entityName]?.size || 'Medium';
                return (
                  <div
                    key={key}
                    className={`kvm-vp-size-cell ${activeKey === key ? 'active' : ''}`}
                    onClick={() => setVar(entityName, 'size', key)}
                  >
                    <div className="kvm-vp-size-label">{label}</div>
                    <div className="kvm-vp-size-price">₹ {(price || 0).toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ice */}
        {hasCold && (
          <div className="kvm-vp-section">
            <div className="kvm-vp-title">Ice Preference</div>
            <div className="kvm-vp-btn-grid">
              <button
                className={`kvm-vp-btn ${varsMap[entityName]?.cold ? 'active' : ''}`}
                onClick={() => setVar(entityName, 'cold', !varsMap[entityName]?.cold)}
              >
                With Ice (+₹{(ev.cold.ice_price || 0).toFixed(2)})
              </button>
            </div>
          </div>
        )}

        {/* Spicy */}
        {hasSpicy && (
          <div className="kvm-vp-section">
            <div className="kvm-vp-title">Spice Level</div>
            <div className="kvm-vp-btn-grid">
              <button
                className={`kvm-vp-btn ${varsMap[entityName]?.spicy ? 'active' : ''}`}
                onClick={() => setVar(entityName, 'spicy', !varsMap[entityName]?.spicy)}
              >
                Spicy (+₹{(ev.spicy.spicy_price || 30).toFixed(2)})
              </button>
            </div>
          </div>
        )}

        {/* Custom Options */}
        {hasCustom && (
          <div className="kvm-vp-section">
            <div className="kvm-vp-title">Custom Options</div>
            <div className="kvm-vp-btn-grid">
              {entity.custom_variants.filter(v => v.enabled).map(v =>
                v.subheadings?.map(sub => (
                  <button
                    key={sub.name}
                    className={`kvm-vp-btn ${cVarsMap[entityName]?.[sub.name] ? 'active' : ''}`}
                    onClick={() => toggleCV(entityName, sub.name)}
                  >
                    {sub.name}{sub.price > 0 ? ` (+₹${sub.price.toFixed(2)})` : ''}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Toppings */}
        {hasToppings && (
          <div className="kvm-vp-section">
            <div className="kvm-vp-title">Toppings</div>
            <div className="kvm-vp-btn-grid">
              {entity.toppings.map(t => {
                const tid = t._id || t.topping_name;
                const full = allToppings.find(x => x._id === tid || x.topping_name === tid) || t;
                return (
                  <button
                    key={tid}
                    className={`kvm-vp-btn ${topsMap[entityName]?.[tid] ? 'active' : ''}`}
                    onClick={() => toggleTop(entityName, tid)}
                  >
                    {full.topping_name || tid}{full.price > 0 ? ` (+₹${full.price.toFixed(2)})` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!item) return null;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="kiosk-variant-modal-overlay">
      <div className="kiosk-variant-modal">

          {/* Header */}
          <div className="kiosk-variant-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <h3 style={{ margin: 0 }}>{item.item_name}</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowIngredientsModal(true);
                }}
                className="btn btn-outline-success btn-sm"
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                  borderRadius: '20px', fontSize: '13px', fontWeight: '600',
                  border: '1px solid #10b981', color: '#10b981', background: 'transparent',
                  transition: 'all 0.3s ease', cursor: 'pointer', whiteSpace: 'nowrap'
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <i className="fa-solid fa-leaf" style={{ fontSize: '14px' }}></i>
                Ingredients & Nutrition
              </button>
            </div>
            <button className="kiosk-variant-close" onClick={onClose}>✖</button>
          </div>

          {/* Body */}
        <div className="kiosk-variant-body">
          {loading ? (
            <div className="kvm-loading">Loading options…</div>
          ) : (
            <>
              {/* ── Main Item Size ── */}
              {fetchedItem?.variants?.size?.enabled && (
                <div className="kiosk-variant-section">
                  <h4>Variation</h4>
                  <div className="kiosk-size-grid">
                    {[
                      { label: 'Small',  key: 'S', price: fetchedItem.variants.size.small_price },
                      { label: 'Medium', key: 'M', price: fetchedItem.variants.size.medium_price },
                      { label: 'Large',  key: 'L', price: fetchedItem.variants.size.large_price },
                    ].map(({ label, key, price }) => (
                      <div key={key} className={`kiosk-size-card ${selectedSize === key ? 'active' : ''}`} onClick={() => setSelectedSize(key)}>
                        <button className="kiosk-size-btn">{label}</button>
                        <div className="kiosk-size-price">₹ {(price || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Preferences ── */}
              {(fetchedItem?.variants?.cold?.enabled || fetchedItem?.variants?.spicy?.enabled) && (
                <div className="kiosk-variant-section">
                  <h4>Preferences</h4>
                  <div className="kiosk-pref-grid">
                    {fetchedItem?.variants?.cold?.enabled && (
                      <button
                        className={`kiosk-pref-btn ${icePreference === 'with_ice' ? 'active' : ''}`}
                        onClick={() => setIcePreference(icePreference === 'with_ice' ? 'without_ice' : 'with_ice')}
                      >
                        Ice (₹ {(fetchedItem.variants.cold.ice_price || 0).toFixed(2)})
                      </button>
                    )}
                    {fetchedItem?.variants?.spicy?.enabled && (
                      <button
                        className={`kiosk-pref-btn ${isSpicy ? 'active' : ''}`}
                        onClick={() => setIsSpicy(!isSpicy)}
                      >
                        Spicy (+₹ {(fetchedItem.variants.spicy.spicy_price || 30).toFixed(2)})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Custom Variants ── */}
              {fetchedItem?.custom_variants?.some(v => v.enabled && v.subheadings?.length > 0) && (
                <div className="kiosk-variant-section">
                  <h4>Custom Options</h4>
                  <div className="kiosk-pref-grid">
                    {fetchedItem.custom_variants.filter(v => v.enabled).map(v =>
                      v.subheadings?.map(sub => (
                        <button
                          key={sub.name}
                          className={`kiosk-pref-btn ${selectedCustomVariants[sub.name] ? 'active' : ''}`}
                          onClick={() => handleCustomVariantToggle(sub.name)}
                        >
                          {sub.name}{sub.price > 0 ? ` (₹ ${sub.price.toFixed(2)})` : ''}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* ── Toppings ── */}
              {fetchedItem?.toppings?.length > 0 && (
                <div className="kiosk-variant-section">
                  <h4>Toppings</h4>
                  <div className="kiosk-pref-grid">
                    {fetchedItem.toppings.map(t => {
                      const tId = t._id || t.topping_name;
                      const full = allToppings.find(x => x._id === tId || x.topping_name === tId) || t;
                      return (
                        <button
                          key={tId}
                          className={`kiosk-pref-btn ${selectedItemToppings[tId] ? 'active' : ''}`}
                          onClick={() => handleToppingToggle(tId)}
                        >
                          {full.topping_name || tId}{full.price > 0 ? ` (₹ ${full.price.toFixed(2)})` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── ADDONS ── */}
              {fetchedItem?.addons?.length > 0 && (
                <div className="kiosk-variant-section">
                  <h4>Add-ons</h4>
                  <div className="kvm-addon-combo-grid">
                    {fetchedItem.addons.map(addon => {
                      const name = addon.name1 || addon.name;
                      const isSelected = (addonQuantities[name] || 0) > 0;

                      // Normalise price — use variants.size.medium if size is enabled
                      const ev = getEntityVariants(addon);
                      const basePrice = ev.size.enabled
                        ? ev.size.medium_price
                        : (addon.addon_price || addon.price || 0);

                      // Image — avoid displaying alt text on screen
                      const imgSrc = addon.addon_image
                        ? `${BASE_URL}${addon.addon_image}`
                        : addon.image_url ? `${BASE_URL}${addon.image_url}`
                        : addon.image ? `${BASE_URL}${addon.image}` : null;

                      return (
                        <div key={name} className="kvm-card-col">
                          {/* Addon Card */}
                          <div
                            className={`kvm-addon-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleAddon(name)}
                          >
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt=""
                                className="kvm-addon-img"
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="kvm-addon-img kvm-addon-img-placeholder">🍽️</div>
                            )}
                            <div className="kvm-addon-info">
                              <span className="kvm-addon-name">{name}</span>
                              <span className="kvm-addon-price">+₹ {basePrice.toFixed(2)}</span>
                            </div>
                            {isSelected && <div className="kvm-check">✓</div>}
                          </div>

                          {/* Variant Panel appears below the card when selected */}
                          {isSelected && renderVariantPanel(addon, name, 'addon')}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── COMBOS ── */}
              {fetchedItem?.combos?.length > 0 && (
                <div className="kiosk-variant-section">
                  <h4>Combos</h4>
                  <div className="kvm-addon-combo-grid">
                    {fetchedItem.combos.map(combo => {
                      const name = combo.name1 || combo.name;
                      const isSelected = (comboQuantities[name] || 0) > 0;

                      const ev = getEntityVariants(combo);
                      const basePrice = ev.size.enabled
                        ? ev.size.medium_price
                        : (combo.combo_price || combo.price || 0);

                      const imgSrc = combo.combo_image
                        ? `${BASE_URL}${combo.combo_image}`
                        : combo.image_url ? `${BASE_URL}${combo.image_url}`
                        : combo.image ? `${BASE_URL}${combo.image}` : null;

                      return (
                        <div key={name} className="kvm-card-col">
                          {/* Combo Card */}
                          <div
                            className={`kvm-addon-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleCombo(name)}
                          >
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt=""
                                className="kvm-addon-img"
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="kvm-addon-img kvm-addon-img-placeholder">🍽️</div>
                            )}
                            <div className="kvm-addon-info">
                              <span className="kvm-addon-name">{name}</span>
                              <span className="kvm-addon-price">+₹ {basePrice.toFixed(2)}</span>
                            </div>
                            {isSelected && <div className="kvm-check">✓</div>}
                          </div>

                          {/* Variant Panel appears below the card when selected */}
                          {isSelected && renderVariantPanel(combo, name, 'combo')}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Kitchen Notes ── */}
              <div className="kiosk-variant-section">
                <h4>Kitchen Notes</h4>
                <div className="kiosk-pref-grid" style={{ display: 'block' }}>
                  <textarea
                    className="form-control"
                    placeholder="Add any special instructions for the kitchen..."
                    value={kitchenNotes?.main || ''}
                    onChange={(e) => setKitchenNotes(prev => ({ ...prev, main: e.target.value }))}
                    rows="2"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ced4da', resize: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}
                  />
                </div>
              </div>

              {/* No variants fallback */}
              {!fetchedItem?.variants?.size?.enabled &&
                !fetchedItem?.variants?.cold?.enabled &&
                !fetchedItem?.custom_variants?.some(v => v.enabled && v.subheadings?.length > 0) &&
                !fetchedItem?.addons?.length &&
                !fetchedItem?.combos?.length &&
                !fetchedItem?.toppings?.length && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>
                    No additional variations for this item.
                  </div>
                )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="kiosk-variant-footer">
          <div className="kiosk-variant-total">
            <span>₹</span> {totalPrice.toFixed(2)}
          </div>
          <div className="kiosk-variant-actions">
            <button className="kiosk-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="kiosk-btn-save" onClick={handleSave} disabled={loading}>Save</button>
          </div>
        </div>

      </div>

      {/* --- INGREDIENTS & NUTRITION MODAL --- */}
      {showIngredientsModal && (
        <div className="modal-overlay" style={{ zIndex: 1200, position: 'absolute' }} onClick={() => setShowIngredientsModal(false)}>
          <div className="modal-content" style={{ width: '90%', maxWidth: '1000px', height: '90vh', overflowY: 'auto', padding: '24px', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #f8fafc', paddingBottom: '16px' }}>
              <h6 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fa-solid fa-leaf" style={{ color: '#10b981', fontSize: '16px' }}></i> Ingredients & Nutrition
              </h6>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {fetchedItem.variants?.size?.enabled && (
                  <>
                    <button
                      style={{
                        padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer',
                        background: selectedSizeFilter === currentSizeFilter ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#f1f5f9',
                        color: selectedSizeFilter === currentSizeFilter ? '#fff' : '#64748b'
                      }}
                      onClick={() => setSelectedSizeFilter(currentSizeFilter)}
                    >
                      {currentSizeFilter.charAt(0).toUpperCase() + currentSizeFilter.slice(1)} Size
                    </button>
                    <button
                      style={{
                        padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer',
                        background: !selectedSizeFilter ? 'linear-gradient(135deg, #10b981, #059669)' : '#f1f5f9',
                        color: !selectedSizeFilter ? '#fff' : '#64748b'
                      }}
                      onClick={() => setSelectedSizeFilter(null)}
                    >
                      Show All
                    </button>
                  </>
                )}
                <button className="kiosk-variant-close" style={{ position: 'static' }} onClick={() => setShowIngredientsModal(false)}>✖</button>
              </div>
            </div>

            {fetchedItem.ingredients.length === 0 &&
              Object.values(addonIngredients).every(ings => ings.length === 0) &&
              Object.values(comboIngredients).every(ings => ings.length === 0) ? (
              <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px' }}>
                <i className="fa-solid fa-box-open" style={{ fontSize: '30px', color: '#cbd5e1', marginBottom: '10px' }}></i>
                <p style={{ margin: 0, color: '#64748b', fontWeight: '500' }}>No ingredients or nutrition data available.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)' }}>
                {selectedSizeFilter ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Nutrition</th>
                        {fetchedItem.ingredients.map((ingredient, index) => (
                          <th key={`main-${index}`} style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>{ingredient.name}</th>
                        ))}
                        {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                          ingredients.map((ingredient, index) => (
                            <th key={`addon-${addonName}-${index}`} style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>
                              {ingredient.name} <span style={{ fontSize: '10px', background: '#ede9fe', padding: '2px 6px', borderRadius: '4px', verticalAlign: 'middle', marginLeft: '4px' }}>{addonName}</span>
                            </th>
                          ))
                        )}
                        {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                          ingredients.map((ingredient, index) => (
                            <th key={`combo-${comboName}-${index}`} style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>
                              {ingredient.name} <span style={{ fontSize: '10px', background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', verticalAlign: 'middle', marginLeft: '4px' }}>{comboName}</span>
                            </th>
                          ))
                        )}
                        <th style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Total</th>
                      </tr>
                      <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Portion Size (gm)</td>
                        {fetchedItem.ingredients.map((ingredient, ingIndex) => (
                          <td key={`main-size-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#0284c7' }}>
                            {ingredient[selectedSizeFilter] || 0} gm
                          </td>
                        ))}
                        {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                          ingredients.map((ingredient, ingIndex) => (
                            <td key={`addon-size-${addonName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#7c3aed' }}>
                              {ingredient[selectedSizeFilter] || 0} gm
                            </td>
                          ))
                        )}
                        {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                          ingredients.map((ingredient, ingIndex) => (
                            <td key={`combo-size-${comboName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#d97706' }}>
                              {ingredient[selectedSizeFilter] || 0} gm
                            </td>
                          ))
                        )}
                        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#059669' }}>-</td>
                      </tr>
                    </thead>
                    <tbody>
                      {getAllNutritionNames.map((nutritionName, nutIndex) => {
                        const hasNutrition = [
                          ...(Array.isArray(fetchedItem?.ingredients) ? fetchedItem.ingredients : []),
                          ...Object.values(addonIngredients).flat(),
                          ...Object.values(comboIngredients).flat(),
                        ].some(ing => ing.nutrition?.some(nut => nut.nutrition_name === nutritionName));
                        if (!hasNutrition) return null;
                        
                        const isEven = nutIndex % 2 === 0;
                        return (
                          <tr key={nutIndex} style={{ backgroundColor: isEven ? '#ffffff' : '#f8fafc', transition: 'background 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isEven ? '#ffffff' : '#f8fafc'}>
                            <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#334155', borderBottom: '1px solid #f1f5f9' }}>{nutritionName}</td>
                            {fetchedItem.ingredients.map((ingredient, ingIndex) => {
                              const nutritionIndex = ingredient.nutrition?.findIndex(nut => nut.nutrition_name === nutritionName) ?? -1;
                              if (nutritionIndex === -1) return <td key={`main-${ingIndex}`} style={{ padding: '12px 16px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>-</td>;
                              const scaledValues = calculateNutrition(ingredient, selectedSizeFilter);
                              return (
                                <td key={`main-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                  {scaledValues[nutritionIndex]} gm
                                </td>
                              );
                            })}
                            {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                              ingredients.map((ingredient, ingIndex) => {
                                const nutritionIndex = ingredient.nutrition?.findIndex(nut => nut.nutrition_name === nutritionName) ?? -1;
                                if (nutritionIndex === -1) return <td key={`addon-${addonName}-${ingIndex}`} style={{ padding: '12px 16px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>-</td>;
                                const scaledValues = calculateNutrition(ingredient, selectedSizeFilter, true, addonName);
                                return (
                                  <td key={`addon-${addonName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                    {scaledValues[nutritionIndex]} gm
                                  </td>
                                );
                              })
                            )}
                            {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                              ingredients.map((ingredient, ingIndex) => {
                                const nutritionIndex = ingredient.nutrition?.findIndex(nut => nut.nutrition_name === nutritionName) ?? -1;
                                if (nutritionIndex === -1) return <td key={`combo-${comboName}-${ingIndex}`} style={{ padding: '12px 16px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>-</td>;
                                const scaledValues = calculateNutrition(ingredient, selectedSizeFilter, false, null, true, comboName);
                                return (
                                  <td key={`combo-${comboName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                    {scaledValues[nutritionIndex]} gm
                                  </td>
                                );
                              })
                            )}
                            <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#059669', borderBottom: '1px solid #f1f5f9', background: '#f0fdf4' }}>
                              {calculateTotalNutritionForSize(selectedSizeFilter, nutritionName)} gm
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ingredient</th>
                        <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nutrition</th>
                        <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Small</th>
                        <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Medium</th>
                        <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Large</th>
                        <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchedItem.ingredients.map((ingredient, ingIndex) => {
                        if (!ingredient.nutrition || ingredient.nutrition.length === 0) {
                          return (
                            <tr key={`main-${ingIndex}-no-nut`} style={{ borderBottom: '2px solid #f1f5f9' }}>
                              <td style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                <div style={{ fontWeight: '700', color: '#0ea5e9', fontSize: '15px' }}>{ingredient.name}</div>
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', background: '#f8fafc', display: 'inline-block', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</div>
                              </td>
                              <td style={{ padding: '16px', color: '#94a3b8' }}>-</td>
                              <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.small || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                              <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.medium || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                              <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.large || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                              <td style={{ padding: '16px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8' }}>-</td>
                            </tr>
                          );
                        }
                        return ingredient.nutrition.map((nut, nutIndex) => (
                          <tr key={`main-${ingIndex}-${nutIndex}`} style={{ borderBottom: nutIndex === ingredient.nutrition.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                            {nutIndex === 0 && (
                              <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', background: '#fafcff' }}>
                                <div style={{ fontWeight: '700', color: '#0ea5e9', fontSize: '15px' }}>{ingredient.name}</div>
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', background: '#f1f5f9', display: 'inline-block', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</div>
                              </td>
                            )}
                            <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>{nut.nutrition_name}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                              {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.small || 0} gm</div>}
                              {calculateNutrition(ingredient, 'small')[nutIndex]} gm
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                              {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.medium || 0} gm</div>}
                              {calculateNutrition(ingredient, 'medium')[nutIndex]} gm
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                              {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.large || 0} gm</div>}
                              {calculateNutrition(ingredient, 'large')[nutIndex]} gm
                            </td>
                            {nutIndex === 0 && (
                              <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#059669', background: '#f0fdf4', fontSize: '15px', borderLeft: '1px solid #dcfce7' }}>
                                {calculateTotalNutritionForSize(currentSizeFilter, nut.nutrition_name)} gm
                              </td>
                            )}
                          </tr>
                        ));
                      })}
                      
                      {/* Addons Formatting */}
                      {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                        ingredients.map((ingredient, ingIndex) => {
                          if (!ingredient.nutrition || ingredient.nutrition.length === 0) {
                            return (
                              <tr key={`addon-${addonName}-${ingIndex}-no-nut`} style={{ borderBottom: '2px solid #f1f5f9' }}>
                                <td style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                  <div style={{ fontWeight: '700', color: '#8b5cf6', fontSize: '15px' }}>{ingredient.name}</div>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                    <span style={{ fontSize: '11px', color: '#6d28d9', background: '#ede9fe', padding: '3px 8px', borderRadius: '12px' }}>{addonName}</span>
                                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                  </div>
                                </td>
                                <td style={{ padding: '16px', color: '#94a3b8' }}>-</td>
                                <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.small || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.medium || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.large || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                <td style={{ padding: '16px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8' }}>-</td>
                              </tr>
                            );
                          }
                          return ingredient.nutrition.map((nut, nutIndex) => (
                            <tr key={`addon-${addonName}-${ingIndex}-${nutIndex}`} style={{ borderBottom: nutIndex === ingredient.nutrition.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                              {nutIndex === 0 && (
                                <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', background: '#faf5ff' }}>
                                  <div style={{ fontWeight: '700', color: '#8b5cf6', fontSize: '15px' }}>{ingredient.name}</div>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                    <span style={{ fontSize: '11px', color: '#6d28d9', background: '#ede9fe', padding: '3px 8px', borderRadius: '12px' }}>{addonName}</span>
                                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                  </div>
                                </td>
                              )}
                              <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>{nut.nutrition_name}</td>
                              <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.small || 0} gm</div>}
                                {calculateNutrition(ingredient, 'small', true, addonName)[nutIndex]} gm
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.medium || 0} gm</div>}
                                {calculateNutrition(ingredient, 'medium', true, addonName)[nutIndex]} gm
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.large || 0} gm</div>}
                                {calculateNutrition(ingredient, 'large', true, addonName)[nutIndex]} gm
                              </td>
                              {nutIndex === 0 && (
                                <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#059669', background: '#f0fdf4', fontSize: '15px', borderLeft: '1px solid #dcfce7' }}>
                                  {calculateTotalNutritionForSize(currentSizeFilter, nut.nutrition_name)} gm
                                </td>
                              )}
                            </tr>
                          ));
                        })
                      )}
                      
                      {/* Combos Formatting */}
                      {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                        ingredients.map((ingredient, ingIndex) => {
                          if (!ingredient.nutrition || ingredient.nutrition.length === 0) {
                            return (
                              <tr key={`combo-${comboName}-${ingIndex}-no-nut`} style={{ borderBottom: '2px solid #f1f5f9' }}>
                                <td style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                  <div style={{ fontWeight: '700', color: '#f59e0b', fontSize: '15px' }}>{ingredient.name}</div>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                    <span style={{ fontSize: '11px', color: '#b45309', background: '#fef3c7', padding: '3px 8px', borderRadius: '12px' }}>{comboName}</span>
                                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                  </div>
                                </td>
                                <td style={{ padding: '16px', color: '#94a3b8' }}>-</td>
                                <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.small || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.medium || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.large || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                <td style={{ padding: '16px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8' }}>-</td>
                              </tr>
                            );
                          }
                          return ingredient.nutrition.map((nut, nutIndex) => (
                            <tr key={`combo-${comboName}-${ingIndex}-${nutIndex}`} style={{ borderBottom: nutIndex === ingredient.nutrition.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                              {nutIndex === 0 && (
                                <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', background: '#fffbeb' }}>
                                  <div style={{ fontWeight: '700', color: '#f59e0b', fontSize: '15px' }}>{ingredient.name}</div>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                    <span style={{ fontSize: '11px', color: '#b45309', background: '#fef3c7', padding: '3px 8px', borderRadius: '12px' }}>{comboName}</span>
                                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                  </div>
                                </td>
                              )}
                              <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>{nut.nutrition_name}</td>
                              <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.small || 0} gm</div>}
                                {calculateNutrition(ingredient, 'small', false, null, true, comboName)[nutIndex]} gm
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.medium || 0} gm</div>}
                                {calculateNutrition(ingredient, 'medium', false, null, true, comboName)[nutIndex]} gm
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.large || 0} gm</div>}
                                {calculateNutrition(ingredient, 'large', false, null, true, comboName)[nutIndex]} gm
                              </td>
                              {nutIndex === 0 && (
                                <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#059669', background: '#f0fdf4', fontSize: '15px', borderLeft: '1px solid #dcfce7' }}>
                                  {calculateTotalNutritionForSize(currentSizeFilter, nut.nutrition_name)} gm
                                </td>
                              )}
                            </tr>
                          ));
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default KioskVariantModal;
